function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

function setCalculatorQuantity(calculator, quantity) {
  const scope = calculator.closest('product-info') || document;
  let quantityInput = scope.querySelector(
    'quantity-input input[name="quantity"], input.quantity__input[name="quantity"], form[action*="/cart/add"] input[name="quantity"]'
  );

  if (!quantityInput) {
    const productForm = scope.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    productForm.appendChild(quantityInput);
  }

  quantityInput.value = String(quantity);
  quantityInput.setAttribute('value', String(quantity));
  quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
  quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
}

class WallpaperCalculator extends HTMLElement {
  // Metres for walls/rolls, centimetres for repeat/offset; calculate in integer mm.
  // Missing pattern metadata intentionally means free match.
  static estimate(variant, state) {
    if (!state?.valid || !Array.isArray(state.walls) || !state.walls.length || state.walls.length > 20) return null;
    const width = Math.round(Number(variant.rollWidth) * 1000);
    const length = Math.round(Number(variant.rollLength) * 1000);
    const repeat = Math.round(Number(variant.patternRepeat ?? 0) * 10);
    const offset = Math.round(Number(variant.patternOffset ?? 0) * 10);
    if (![width, length, repeat, offset].every(Number.isSafeInteger) || width <= 0 || length <= 0 ||
        repeat < 0 || offset < 0 || (offset > 0 && (repeat === 0 || offset >= repeat))) return null;
    const cuts = [];
    for (const wall of state.walls) {
      const w = Math.round(Number(wall.width) * 1000), h = Math.round(Number(wall.height) * 1000);
      if (![w, h].every(Number.isSafeInteger) || w <= 0 || h <= 0 || w > 100000 || h > 20000) return null;
      const count = Math.ceil(w / width), cut = h + 100; // 5 cm top + 5 cm bottom.
      if (cut > length || (repeat && cut + repeat > length) || cuts.length + count > 10000) return null;
      for (let i = 0; i < count; i++) cuts.push({ length: cut, phase: repeat ? i * offset % repeat : 0 });
    }
    // Longer walls first. Reuse only complete drops, never width offcuts or joined drops.
    cuts.sort((a, b) => b.length - a.length || a.phase - b.phase);
    const rolls = [];
    for (const cut of cuts) {
      let best = null, waste = 0;
      for (const roll of rolls) {
        const gap = repeat ? (cut.phase - roll.phase + repeat) % repeat : 0;
        if (roll.remaining >= gap + cut.length &&
            (!best || roll.remaining - gap < best.remaining - waste)) { best = roll; waste = gap; }
      }
      if (!best) {
        // One repeat per new roll covers the unknown initial pattern position.
        best = { remaining: length - repeat, phase: cut.phase };
        rolls.push(best);
        waste = 0;
      }
      best.remaining -= waste + cut.length;
      best.phase = repeat ? (cut.phase + cut.length) % repeat : 0;
    }
    return { quantity: rolls.length, drops: cuts.length };
  }

  static recommend(variants, state) {
    if (!Array.isArray(variants)) return null;
    // Never choose a different colour/design behind the customer's back.
    if (variants.length > 1 && variants.some(v => !Array.isArray(v.options) || v.options.length !== 1 ||
        !/^\s*\d+(?:[.,]\d+)?\s*(?:cm|m)\s*[x×]\s*\d+(?:[.,]\d+)?\s*(?:cm|m)\s*$/i.test(v.options[0]))) return null;
    const offers = [];
    for (const variant of variants) {
      const id = Number(variant.id), price = Number(variant.priceCents);
      const max = Math.min(variant.stock ?? Infinity, variant.max ?? Infinity);
      if (!variant.available || !Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(price) || price < 0 ||
          (variant.min ?? 1) !== 1 || (variant.increment ?? 1) !== 1 || !(max >= 1)) continue;
      const estimate = this.estimate(variant, state);
      if (estimate && estimate.quantity <= max) offers.push({ ...estimate, id, price, max,
        rollWidth: Number(variant.rollWidth), rollLength: Number(variant.rollLength), total: estimate.quantity * price });
    }
    offers.sort((a, b) => a.total - b.total || a.quantity - b.quantity || a.id - b.id);
    return offers[0] || null;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();
    const listenerOptions = { signal: this.abortController.signal };
    try { this.variantData = JSON.parse(this.querySelector('[data-wallpaper-variants]')?.textContent || '{}'); }
    catch (_) { this.variantData = {}; }
    this.variantId = this.getCurrentVariantId();
    this.addEventListener('input', () => this.calculate(), listenerOptions);
    this.addEventListener('click', event => {
      const help = event.target.closest('[data-wallpaper-help]');
      if (help) {
        const info = this.querySelector('[data-wallpaper-info]');
        info.hidden = !info.hidden;
        this.querySelectorAll('[data-wallpaper-help]').forEach(button => button.setAttribute('aria-expanded', String(!info.hidden)));
      }
      if (event.target.closest('[data-wallpaper-add]')) {
        this.addWall();
        this.calculate();
        this.querySelector('[data-wallpaper-walls]').lastElementChild.querySelector('input').focus();
      }
      const remove = event.target.closest('[data-wallpaper-remove]');
      if (remove && this.querySelectorAll('[data-wallpaper-wall]').length > 1) {
        remove.closest('[data-wallpaper-wall]').remove();
        this.calculate();
        this.querySelector('[data-wallpaper-add]').focus();
      }
    }, listenerOptions);
    if (!this.hasAttribute('data-advisor-calculator')) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const info = this.closest('product-info');
        const sectionId = info?.dataset.originalSection || info?.dataset.section;
        if (!data?.variant || (sectionId && data.sectionId !== sectionId)) return;
        this.variantId = String(data.variant.id);
        this.calculate();
      });
    }
    this.calculate();
  }
  disconnectedCallback() {
    this.abortController?.abort();
    this.variantChangeUnsubscriber?.();
    this.initialized = false;
  }
  getCurrentVariantId() {
    return this.closest('product-info')?.querySelector('form[action*="/cart/add"] [name="id"]')?.value || this.dataset.initialVariantId || '';
  }
  addWall() {
    const rows = this.querySelector('[data-wallpaper-walls]');
    if (rows.children.length >= 20) return;
    const row = rows.firstElementChild.cloneNode(true);
    row.querySelectorAll('input').forEach(input => { input.value = ''; });
    rows.appendChild(row);
  }
  restore(state) {
    const walls = Array.isArray(state.walls) && state.walls.length ? state.walls.slice(0, 20) : [{ width: '', height: '' }];
    const rows = this.querySelector('[data-wallpaper-walls]');
    while (rows.children.length > 1) rows.lastElementChild.remove();
    walls.forEach((wall, i) => {
      if (i) this.addWall();
      const row = rows.children[i];
      row.querySelector('[data-wall-width]').value = wall.width;
      row.querySelector('[data-wall-height]').value = wall.height;
    });
  }
  readState() {
    const rows = [...this.querySelectorAll('[data-wallpaper-wall]')];
    return {
      walls: rows.map(row => ({ width: Number(row.querySelector('[data-wall-width]').value), height: Number(row.querySelector('[data-wall-height]').value) })),
      valid: rows.length > 0 && rows.every(row => [...row.querySelectorAll('input')].every(input => input.value !== '' && input.checkValidity() && Number(input.value) > 0))
    };
  }
  calculate() {
    const state = this.readState(), result = this.querySelector('[data-wallpaper-result]');
    const total = this.querySelector('[data-wallpaper-total]');
    this.querySelectorAll('[data-wallpaper-remove]').forEach(button => { button.hidden = state.walls.length === 1; });
    this.querySelector('[data-wallpaper-add]').disabled = state.walls.length >= 20;
    total.textContent = '';
    if (this.hasAttribute('data-advisor-calculator')) {
      result.textContent = state.valid ? 'Die passende Rollenmenge berechnen wir für jede Tapete in Deinen Ergebnissen.' : '';
      this.dispatchEvent(new CustomEvent('wallpaper-area-change', { bubbles: true, detail: state }));
      return;
    }
    if (!state.valid) {
      const hasInput = [...this.querySelectorAll('[data-wallpaper-wall] input')].some(input => input.value !== '' || input.validity.badInput);
      result.textContent = hasInput ? 'Bitte gib Breite und Höhe Deiner Wände ein.' : '';
      setCalculatorQuantity(this, 1);
      return;
    }
    const variant = this.variantData[this.variantId], estimate = variant && WallpaperCalculator.estimate(variant, state);
    if (!estimate) { result.textContent = 'Für diese Maße und Rollengröße ist keine sichere Rollenberechnung möglich.'; setCalculatorQuantity(this, 1); return; }
    result.textContent = 'Gesamt: ' + estimate.quantity + (estimate.quantity === 1 ? ' Rolle' : ' Rollen');
    total.textContent = new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(estimate.quantity * variant.priceCents / 100);
    setCalculatorQuantity(this, estimate.quantity);
  }
}

if (!customElements.get('wallpaper-calculator')) {
  customElements.define('wallpaper-calculator', WallpaperCalculator);
}

class AreaCoverageCalculator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.widthInput = this.querySelector('[data-coverage-width]');
    this.heightInput = this.querySelector('[data-coverage-height]');
    this.result = this.querySelector('[data-coverage-result]');
    this.total = this.querySelector('[data-coverage-total]');
    this.error = this.querySelector('[data-coverage-error]');
    this.helpButton = this.querySelector('[data-coverage-help]');
    this.info = this.querySelector('[data-coverage-info]');
    this.closeButton = this.querySelector('[data-coverage-close]');
    this.variantData = this.getVariantData();
    this.variantId = this.getCurrentVariantId();

    this.abortController = new AbortController();
    const listenerOptions = { signal: this.abortController.signal };

    this.widthInput?.addEventListener('input', () => this.calculate(), listenerOptions);
    this.heightInput?.addEventListener('input', () => this.calculate(), listenerOptions);
    this.helpButton?.addEventListener('click', () => this.toggleInfo(), listenerOptions);
    this.closeButton?.addEventListener('click', () => this.closeInfo(), listenerOptions);
    document.addEventListener('click', (event) => this.handleDocumentClick(event), listenerOptions);

    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) =>
      this.handleVariantChange(event)
    );

    this.calculate();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.variantChangeUnsubscriber?.();
    this.initialized = false;
  }

  getVariantData() {
    const dataElement = this.querySelector('[data-coverage-variants]');
    if (!dataElement) return {};

    try {
      return JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('Invalid area calculator variant data', error);
      return {};
    }
  }

  getCurrentVariantId() {
    const scope = this.closest('product-info') || document;
    const variantInput = scope.querySelector('form[action*="/cart/add"] [name="id"]');
    return variantInput?.value || this.dataset.initialVariantId || '';
  }

  handleVariantChange({ data }) {
    const productInfo = this.closest('product-info');
    const sectionId = productInfo?.dataset.originalSection || productInfo?.dataset.section;

    if (!data?.variant || (sectionId && data.sectionId !== sectionId)) return;

    this.variantId = String(data.variant.id);
    this.calculate();
  }

  toggleInfo() {
    if (!this.info || !this.helpButton) return;

    const shouldOpen = this.info.hidden;
    this.info.hidden = !shouldOpen;
    this.helpButton.setAttribute('aria-expanded', String(shouldOpen));
  }

  closeInfo() {
    if (!this.info || !this.helpButton) return;

    this.info.hidden = true;
    this.helpButton.setAttribute('aria-expanded', 'false');
  }

  handleDocumentClick(event) {
    if (!this.info || this.info.hidden || this.contains(event.target)) return;
    this.closeInfo();
  }

  showError(message) {
    if (this.result) this.result.textContent = 'Nicht verfügbar';
    if (this.total) this.total.textContent = '';
    if (this.error) {
      this.error.textContent = message;
      this.error.hidden = false;
    }
  }

  clearError() {
    if (!this.error) return;
    this.error.textContent = '';
    this.error.hidden = true;
  }

  calculate() {
    if (!this.widthInput || !this.heightInput || !this.result || !this.total) return;

    const variant = this.variantData[this.variantId] || {};
    const coverage = Number.parseFloat(variant.coverage) || 0;
    const priceCents = Number.parseFloat(variant.priceCents) || 0;

    if (coverage <= 0) {
      this.showError(this.dataset.missingCoverageMessage || 'Für die gewählte Variante fehlt die Reichweite.');
      setCalculatorQuantity(this, 1);
      return;
    }

    const wallWidth = Number.parseFloat(this.widthInput.value);
    const wallHeight = Number.parseFloat(this.heightInput.value);

    if (!wallWidth || !wallHeight || wallWidth <= 0 || wallHeight <= 0) {
      this.clearError();
      this.result.textContent = `1 ${this.dataset.unitSingular}`;
      this.total.textContent = '';
      setCalculatorQuantity(this, 1);
      return;
    }

    const coverageRatio = (wallWidth * wallHeight) / coverage;
    const roundingTolerance = Number.EPSILON * Math.max(1, Math.abs(coverageRatio)) * 8;
    const units = Math.ceil(coverageRatio - roundingTolerance);
    this.clearError();
    const unit = units === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;
    this.result.textContent = `${units} ${unit}`;
    const formattedTotal = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format((units * priceCents) / 100);
    this.total.textContent = `Gesamt: ${formattedTotal}`;
    setCalculatorQuantity(this, units);
  }
}

class AdhesiveFilmCalculator extends AreaCoverageCalculator {
  // Shared by the product page and Kaufberater. Inputs are centimetres, metadata metres.
  static estimate(variants, state) {
    if (!state?.valid || !state.surfaces?.length) return null;
    const offers = (variants || []).flatMap(v => {
      const width = Math.round(Number(v.rollWidth) * 10000);
      const length = Math.round(Number(v.rollLength) * 10000);
      const max = Math.floor(Math.min(v.stock ?? Infinity, v.max ?? Infinity));
      // Do not guess orientation for suspicious/incomplete catalogue data.
      if (!v.available || !Number.isSafeInteger(v.id) || !Number.isSafeInteger(v.priceCents) ||
          v.priceCents < 0 || !(Number.isSafeInteger(width) && Number.isSafeInteger(length) && width > 0 && length >= width) || !(max >= 1) ||
          (v.min ?? 1) !== 1 || (v.increment ?? 1) !== 1) return [];
      let quantity = 0, seams = 0;
      for (const surface of state.surfaces) {
        const w = Math.round(surface.width * 100);
        const h = Math.round(surface.height * 100);
        if (!(w > 0 && h > 0 && Number.isInteger(surface.count) && surface.count > 0)) return [];
        if (h > length) return []; // Never join short lengths or rotate the grain.
        const strips = Math.ceil(w / width);
        const rows = Math.floor(length / h);
        const capacity = strips === 1 ? rows * Math.floor(width / w) : rows;
        quantity += Math.ceil(surface.count * strips / capacity);
        seams += (strips - 1) * surface.count;
      }
      if (!Number.isSafeInteger(quantity) || quantity > max) return [];
      return [{ id: v.id, quantity, seams, max, price: v.priceCents,
        rollWidth: width / 10000, rollLength: length / 10000, total: quantity * v.priceCents }];
    }).sort((a, b) => a.seams - b.seams || a.total - b.total || a.quantity - b.quantity || a.id - b.id);
    return offers.length ? { best: offers[0], offers } : null;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.result = this.querySelector('[data-coverage-result]');
    this.total = this.querySelector('[data-coverage-total]');
    this.error = this.querySelector('[data-coverage-error]');
    this.recommendation = this.querySelector('[data-film-recommendation]');
    this.applyButton = this.querySelector('[data-film-apply]');
    this.variantData = this.getVariantData();
    this.variantId = this.getCurrentVariantId();
    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };
    this.addEventListener('input', () => this.calculate(), options);
    this.addEventListener('change', () => this.calculate(), options);
    this.addEventListener('click', event => {
      if (event.target.closest('[data-film-add]')) {
        const rows = this.querySelector('[data-film-surfaces]');
        if (rows.children.length >= 8) return;
        const row = rows.firstElementChild.cloneNode(true);
        row.querySelectorAll('input').forEach(input => { input.value = input.hasAttribute('data-film-count') ? '1' : ''; });
        row.querySelector('[data-film-remove]').hidden = false;
        rows.append(row); row.querySelector('input').focus(); this.calculate();
      }
      if (event.target.closest('[data-film-remove]')) {
        event.target.closest('[data-film-surface]').remove(); this.calculate();
        this.querySelector('[data-film-add]').focus();
      }
      if (event.target.closest('[data-film-apply]')) this.applyRecommendation();
    }, options);
    if (!this.hasAttribute('data-advisor-calculator')) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, event => this.handleVariantChange(event));
    }
    this.calculate();
  }

  readState() {
    const surfaces = [...this.querySelectorAll('[data-film-surface]')].map(row => ({
      width: Number(row.querySelector('[data-coverage-width]').value),
      height: Number(row.querySelector('[data-coverage-height]').value),
      count: Number(row.querySelector('[data-film-count]').value)
    }));
    const valid = [...this.querySelectorAll('input[type="number"]')].every(input => input.value !== '' && input.checkValidity()) &&
      surfaces.length > 0 && surfaces.length <= 8 && surfaces.every(s => s.width > 0 && s.height > 0 && Number.isInteger(s.count) && s.count > 0);
    return { surfaces, valid };
  }

  restore(state) {
    const rows = this.querySelector('[data-film-surfaces]');
    for (let i = 1; i < (state.surfaces?.length || 1); i++) {
      const row = rows.firstElementChild.cloneNode(true);
      row.querySelector('[data-film-remove]').hidden = false; rows.append(row);
    }
    [...rows.children].forEach((row, i) => {
      const value = state.surfaces?.[i] || {};
      row.querySelector('[data-coverage-width]').value = value.width || '';
      row.querySelector('[data-coverage-height]').value = value.height || '';
      row.querySelector('[data-film-count]').value = value.count || 1;
    });
  }

  calculate() {
    if (!this.result) return;
    const state = this.readState();
    this.querySelector('[data-film-add]').disabled = state.surfaces.length >= 8;
    this.clearError();
    this.applyButton.hidden = true;
    this.recommendation.textContent = '';
    this.total.textContent = '';
    if (this.hasAttribute('data-advisor-calculator')) {
      const area = state.surfaces.reduce((sum, s) => sum + s.width * s.height * s.count / 10000, 0);
      this.result.textContent = state.valid ? 'Deine Fläche: ' + this.number(area) + ' m². Die passende Rollengröße berechnen wir je Produkt im Ergebnis.' : 'Bitte gib Breite und Höhe Deiner Fläche ein.';
      this.dispatchEvent(new CustomEvent('film-area-change', { bubbles: true, detail: state }));
      return;
    }
    if (!state.valid) { this.result.textContent = 'Bitte gib Breite und Höhe Deiner Fläche ein.'; return; }
    const estimate = this.constructor.estimate(Object.values(this.variantData), state);
    const current = estimate?.offers.find(offer => String(offer.id) === String(this.variantId));
    const money = cents => new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
    if (current) {
      this.result.textContent = current.quantity + (current.quantity === 1 ? ' Rolle' : ' Rollen') + ' · ' + (current.seams ? 'Mit Nahtstellen' : 'Ohne Nahtstellen');
      this.total.textContent = 'Gesamt: ' + money(current.total);
      setCalculatorQuantity(this, current.quantity);
    } else this.result.textContent = 'Die gewählte Größe ist für diesen Zuschnitt nicht sicher berechenbar.';
    this.best = estimate?.best;
    if (!this.best) {
      this.recommendation.textContent = 'Keine passende Rollenmenge ermittelt. Bitte prüfe Maße, Rollenlänge und Verfügbarkeit.';
      return;
    }
    if (String(this.best.id) !== String(this.variantId)) {
      this.recommendation.textContent = 'Empfehlung: ' + this.best.quantity + ' × ' + this.number(this.best.rollWidth * 100) + ' × ' +
        this.number(this.best.rollLength * 100) + ' cm · ' + money(this.best.total) + ' · ' + (this.best.seams ? 'Mit Nahtstellen' : 'Ohne Nahtstellen');
      this.applyButton.hidden = false;
    }
  }

  number(value) { return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(value); }

  applyRecommendation() {
    if (!this.best) return;
    const picker = this.closest('product-info')?.querySelector('variant-selects');
    const options = this.variantData[this.best.id]?.options;
    // Existing Shopify option-change flow updates URL, form ID, stock, media and price together.
    const groups = picker ? [...picker.querySelectorAll('fieldset, select')].filter(group => group.querySelector('input[type="radio"], option')) : null;
    if (!groups || !options || groups.length !== options.length) {
      this.recommendation.textContent = 'Bitte wähle die empfohlene Größe über die Produktauswahl.'; return;
    }
    const targets = [...groups].map((group, index) => [...group.querySelectorAll('input[type="radio"], option')].find(el => el.value === options[index]));
    if (targets.some(el => !el)) return;
    targets.forEach(el => { if (el.tagName === 'OPTION') el.parentElement.value = el.value; else el.checked = true; });
    const last = targets.at(-1);
    (last.tagName === 'OPTION' ? last.parentElement : last).dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('paint-calculator')) {
  customElements.define('paint-calculator', class extends AreaCoverageCalculator {
    // Product pages retain the existing one-coat, variant-based bucket calculation.
    connectedCallback() {
      if (!this.hasAttribute('data-advisor-calculator')) return super.connectedCallback();
      if (this.initialized) return;
      this.initialized = true;
      this.modeInput = this.querySelector('[data-coverage-mode]');
      this.areaInput = this.querySelector('[data-coverage-area]');
      this.result = this.querySelector('[data-coverage-result]');
      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };
      this.addEventListener('input', () => this.calculate(), listenerOptions);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-coverage-add]')) {
          const row = this.addWall();
          this.calculate();
          row?.querySelector('input').focus();
        }
        const remove = event.target.closest('[data-coverage-remove]');
        if (remove && this.querySelectorAll('[data-coverage-wall]').length > 1) {
          const row = remove.closest('[data-coverage-wall]');
          const neighbour = row.nextElementSibling || row.previousElementSibling;
          row.remove();
          this.calculate();
          neighbour.querySelector('input').focus();
        }
      }, listenerOptions);
      this.calculate();
    }

    addWall() {
      const rows = this.querySelector('[data-coverage-walls]');
      if (rows.children.length >= 20) return null;
      const row = rows.firstElementChild.cloneNode(true);
      row.querySelectorAll('input').forEach(input => { input.value = ''; });
      rows.appendChild(row);
      return row;
    }

    restore(state) {
      this.querySelector('[data-coverage-mode]').value = state.mode === 'dimensions' ? 'dimensions' : 'area';
      this.querySelector('[data-coverage-area]').value = state.squareMeters || '';
      // Restore older single-wall answers as well as the current wall list.
      const walls = Array.isArray(state.walls) && state.walls.length
        ? state.walls.slice(0, 20) : [{ width: state.width || '', height: state.height || '' }];
      const rows = this.querySelector('[data-coverage-walls]');
      while (rows.children.length > 1) rows.lastElementChild.remove();
      walls.forEach((wall, index) => {
        const row = index ? this.addWall() : rows.firstElementChild;
        row.querySelector('[data-coverage-width]').value = wall.width;
        row.querySelector('[data-coverage-height]').value = wall.height;
      });
    }

    static estimate(products, area) {
      if (!Number.isFinite(area) || area <= 0 || !products?.length) return null;
      const rates = [];
      for (const product of products) {
        const productRates = (product.paintVariants || []).flatMap((variant) => {
          const size = variant.options?.map((option) => String(option).trim().match(/^(\d+(?:[.,]\d+)?)\s*(?:l|liter)$/i)).find(Boolean);
          const litres = size ? Number(size[1].replace(',', '.')) : 0;
          const coverage = Number(variant.coverage);
          return Number.isFinite(coverage) && coverage > 0 && litres > 0 ? [coverage / litres] : [];
        });
        // Never invent a consumption rate for a product with missing size/coverage.
        if (!productRates.length) return null;
        rates.push(...productRates);
      }
      const roundedLitres = (rate) => {
        const litres = area * 2 / rate;
        return Math.ceil(litres - Number.EPSILON * Math.max(1, litres) * 8);
      };
      const estimate = { min: roundedLitres(Math.max(...rates)), max: roundedLitres(Math.min(...rates)) };
      return Number.isFinite(estimate.min) && Number.isFinite(estimate.max) ? estimate : null;
    }

    static formatEstimate(estimate) {
      if (!estimate) return 'Der Literbedarf ist erst mit einer hinterlegten Produktreichweite verfügbar.';
      const format = (n) => new Intl.NumberFormat('de-DE').format(n);
      return 'Du benötigst voraussichtlich ' + format(estimate.min) + (estimate.min === estimate.max ? '' : '–' + format(estimate.max)) + ' Liter.';
    }

    calculate() {
      if (!this.hasAttribute('data-advisor-calculator')) return super.calculate();
      if (!this.areaInput) return;
      const dimensions = this.modeInput.value === 'dimensions';
      this.querySelector('[data-coverage-area-fields]').hidden = dimensions;
      this.querySelector('[data-coverage-dimension-fields]').hidden = !dimensions;
      const rows = [...this.querySelectorAll('[data-coverage-wall]')];
      const walls = rows.map((row, index) => {
        row.setAttribute('aria-label', 'Wand ' + (index + 1));
        const remove = row.querySelector('[data-coverage-remove]');
        remove.hidden = rows.length === 1;
        remove.setAttribute('aria-label', 'Wand ' + (index + 1) + ' entfernen');
        return { width: row.querySelector('[data-coverage-width]').value, height: row.querySelector('[data-coverage-height]').value };
      });
      this.querySelector('[data-coverage-add]').disabled = rows.length >= 20;
      const inputs = dimensions ? rows.flatMap(row => [...row.querySelectorAll('input')]) : [this.areaInput];
      const valid = inputs.length > 0 && inputs.every(input => input.value !== '' && input.checkValidity() && Number.isFinite(Number(input.value)) && Number(input.value) > 0);
      const area = valid ? (dimensions ? walls.reduce((sum, wall) => sum + Number(wall.width) * Number(wall.height), 0) : Number(this.areaInput.value)) : 0;
      const state = { mode: this.modeInput.value, squareMeters: this.areaInput.value, walls, area: Number.isFinite(area) ? area : 0 };
      const estimate = this.constructor.estimate(this.products, state.area);
      const formattedArea = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(state.area);
      this.result.textContent = !state.area
        ? 'Bitte gib eine gültige Fläche ein.'
        : 'Deine Fläche: ' + formattedArea + ' m².' + (estimate ? ' ' + this.constructor.formatEstimate(estimate) : '');
      this.dispatchEvent(new CustomEvent('paint-area-change', { bubbles: true, detail: state }));
    }
  });
}

if (!customElements.get('adhesive-film-calculator')) {
  customElements.define('adhesive-film-calculator', AdhesiveFilmCalculator);
}
